import { processDate, processDateJS } from "../../utils/dateProcessor";

const UTR_DIGITS_LENGTH = 10;
const UTR_SPLIT_INDEX = 5;

export const sanitizeUtr = (value = "") =>
  String(value).replace(/\D/g, "").slice(0, UTR_DIGITS_LENGTH);

export const formatUtr = (value = "") => {
  const digits = sanitizeUtr(value);
  if (!digits) return "";
  if (digits.length <= UTR_SPLIT_INDEX) return digits;
  return `${digits.slice(0, UTR_SPLIT_INDEX)}-${digits.slice(UTR_SPLIT_INDEX)}`;
};

export function normalizeConsolidatedPartnerships(data) {
  if (!data) return [];

  const normalizeDate = (dateValue) => {
    if (!dateValue) return "";
    if (
      typeof dateValue === "string" &&
      dateValue.match(/^\d{4}-\d{2}-\d{2}T/)
    ) {
      return dateValue;
    }

    try {
      return processDate(dateValue) || "";
    } catch (error) {
      return "";
    }
  };

  if (typeof data === "string") {
    return data.trim()
      ? [
          {
            name: data.trim(),
            utr: "",
            date: "",
            disposedDate: "",
            electionSubmitted: false,
          },
        ]
      : [];
  }

  if (Array.isArray(data)) {
    return data
      .map((item) => {
        if (item && typeof item === "object" && "name" in item) {
          return {
            name:
              typeof item.name === "string"
                ? item.name
                : String(item.name ?? ""),
            utr: formatUtr(
              typeof item.utr === "string" ? item.utr : item.utr || "",
            ),
            date: normalizeDate(item.date),
            disposedDate: normalizeDate(item.disposedDate),
            electionSubmitted:
              typeof item.electionSubmitted === "boolean"
                ? item.electionSubmitted
                : item.electionSubmitted || false,
          };
        }

        if (
          item &&
          typeof item === "object" &&
          "name" in item &&
          "poa_date" in item
        ) {
          return {
            name:
              typeof item.name === "string"
                ? item.name
                : String(item.name ?? ""),
            utr: "",
            date: normalizeDate(item.poa_date),
            disposedDate: "",
            electionSubmitted: false,
          };
        }

        if (typeof item === "string") {
          return {
            name: item,
            utr: "",
            date: "",
            disposedDate: "",
            electionSubmitted: false,
          };
        }

        return null;
      })
      .filter((item) => item !== null);
  }

  if (typeof data === "object") {
    if ("name" in data) {
      return [
        {
          name:
            typeof data.name === "string" ? data.name : String(data.name ?? ""),
          utr: formatUtr(
            typeof data.utr === "string" ? data.utr : data.utr || "",
          ),
          date: normalizeDate(data.date || data.poa_date),
          disposedDate: normalizeDate(data.disposedDate),
          electionSubmitted:
            typeof data.electionSubmitted === "boolean"
              ? data.electionSubmitted
              : data.electionSubmitted || false,
        },
      ];
    }
  }

  return [];
}

export function getAllowedPoaPeriodEndDates(poaPeriodEndDates = []) {
  return Array.from(
    new Set(
      (poaPeriodEndDates || [])
        .map((date) => processDate(date))
        .filter((date) => !!date),
    ),
  ).sort((a, b) => processDateJS(a).valueOf() - processDateJS(b).valueOf());
}

export function isAllowedConsolidatedPartnershipDate(
  dateValue,
  allowedPoaPeriodEndDates,
  firstModelStartDate,
) {
  if (!dateValue) return false;

  const selectedDate = processDateJS(dateValue);
  if (!selectedDate) return false;

  const matchesPoaPeriodEnd = allowedPoaPeriodEndDates.some((poaEnd) =>
    processDateJS(poaEnd)?.isSame(selectedDate, "day"),
  );
  if (matchesPoaPeriodEnd) return true;

  if (!firstModelStartDate) return false;

  return selectedDate.isBefore(firstModelStartDate, "day");
}

export function validateConsolidatedPartnershipDateByMode(
  dateValue,
  mode,
  allowedPoaPeriodEndDates,
  firstModelStartDate,
  fieldLabel,
) {
  if (!dateValue) {
    return { isValid: false, error: `${fieldLabel} is required.` };
  }

  const selectedDate = processDateJS(dateValue);
  if (!selectedDate) {
    return {
      isValid: false,
      error: `Please enter a valid ${fieldLabel.toLowerCase()}.`,
    };
  }

  if (mode === "poa") {
    const isPoaDate = allowedPoaPeriodEndDates.some((poaEnd) =>
      processDateJS(poaEnd)?.isSame(selectedDate, "day"),
    );
    if (!isPoaDate) {
      return {
        isValid: false,
        error: "Choose one of the available PoA period end dates.",
      };
    }
    return { isValid: true, error: "" };
  }

  if (!firstModelStartDate) {
    return {
      isValid: false,
      error:
        "Custom date is unavailable because the first model PoA start date is missing.",
    };
  }

  if (!selectedDate.isBefore(firstModelStartDate, "day")) {
    return {
      isValid: false,
      error: `Custom date must be before ${firstModelStartDate.format("DD/MM/YYYY")}.`,
    };
  }

  return { isValid: true, error: "" };
}

export function getConsolidatedPartnershipDuplicateErrors(partnerships = []) {
  const normalizedPartnerships =
    normalizeConsolidatedPartnerships(partnerships);
  const normalizedNames = normalizedPartnerships.map((partnership) =>
    String(partnership?.name || "")
      .trim()
      .toLowerCase(),
  );

  return normalizedPartnerships.map((partnership, index) => {
    const currentName = normalizedNames[index];
    if (!currentName) return true;

    return normalizedNames.some(
      (name, otherIndex) =>
        otherIndex !== index && name && name === currentName,
    );
  });
}

export function validateConsolidatedPartnerships({
  partnerships = [],
  poaPeriodEndDates = [],
  firstModelPeriodStartDate,
  requireAtLeastOne = true,
  requireUniqueNames = true,
}) {
  const normalizedPartnerships =
    normalizeConsolidatedPartnerships(partnerships);
  const allowedPoaPeriodEndDates =
    getAllowedPoaPeriodEndDates(poaPeriodEndDates);
  const firstModelStartDate = firstModelPeriodStartDate
    ? processDateJS(firstModelPeriodStartDate)
    : null;

  const hasNoPartnerships =
    requireAtLeastOne && normalizedPartnerships.length === 0;
  const hasMissingRequiredFields = normalizedPartnerships.some(
    (partnership) =>
      typeof partnership?.name !== "string" ||
      partnership.name.trim() === "" ||
      !partnership?.date ||
      (typeof partnership.date === "string" && partnership.date.trim() === ""),
  );
  const duplicateErrors = getConsolidatedPartnershipDuplicateErrors(
    normalizedPartnerships,
  );
  const hasDuplicateNames = requireUniqueNames
    ? duplicateErrors.some((value) => value === true)
    : false;

  const hasInvalidStartDate = normalizedPartnerships.some(
    (partnership) =>
      partnership?.date &&
      !isAllowedConsolidatedPartnershipDate(
        partnership.date,
        allowedPoaPeriodEndDates,
        firstModelStartDate,
      ),
  );

  return {
    normalizedPartnerships,
    duplicateErrors,
    hasNoPartnerships,
    hasMissingRequiredFields,
    hasDuplicateNames,
    hasInvalidStartDate,
    hasErrors:
      hasNoPartnerships ||
      hasMissingRequiredFields ||
      hasDuplicateNames ||
      hasInvalidStartDate,
  };
}

export function deriveConsolidatedPartnershipElection(
  partnerships = [],
  periodEndDate,
) {
  const normalizedPartnerships =
    normalizeConsolidatedPartnerships(partnerships);
  const periodEnd = processDateJS(periodEndDate);

  if (!periodEnd || normalizedPartnerships.length === 0) {
    return {
      switchValue: false,
      yearValue: null,
    };
  }

  const validDates = normalizedPartnerships
    .map((partnership) => processDateJS(partnership?.date))
    .filter((date) => date && date.isSameOrBefore(periodEnd, "day"))
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (validDates.length === 0) {
    return {
      switchValue: false,
      yearValue: null,
    };
  }

  return {
    switchValue: true,
    yearValue: processDate(validDates[0].toDate()),
  };
}
